import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Select,
  Text,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import UserSelect from "features/user-select/user-select";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import {
  createIncident,
  updateIncident,
  IncidentDetail,
  IncidentType,
  IncidentSeverity,
  IncidentWitness,
} from "services/work-security-incidents.service";
import { WorkSecurityArea } from "services/work-security-areas.service";
import AreaFormField, {
  defaultAreaSelection,
} from "features/work-security-areas/AreaFormField";
import { resolveAreaErrorMessage } from "features/work-security-areas/area-errors";

interface IncidentFormModalProps {
  /** When present the modal edits an existing incident; otherwise it creates one. */
  incident?: IncidentDetail;
  /**
   * Accessible spheres that track incidents (PR-2 §P4 — CREATE only: the
   * incident detail/update endpoints don't return or accept `area` at all,
   * so an existing incident's sphere can neither be shown nor changed here;
   * flagged as a backend-contract gap in the code comment below).
   */
  areas?: WorkSecurityArea[];
  /** The module header's concretely-selected sphere; null/undefined = "all spheres" mode. */
  selectedArea?: WorkSecurityArea | null;
  onSuccess?: (incidentId?: string) => void;
}

function msToLocalInput(ms: number | null): string {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

export default function IncidentFormModal({
  incident,
  areas = [],
  selectedArea,
  onSuccess,
}: IncidentFormModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const isEdit = !!incident;
  // CREATE only — see the prop doc above for why edit gets no sphere field.
  const [selectedAreaId, setSelectedAreaId] = useState(() => defaultAreaSelection(areas));
  const showAreaField = !isEdit && areas.length > 1;

  const [type, setType] = useState<IncidentType>(incident?.type || "incident");
  const [severity, setSeverity] = useState<IncidentSeverity>(
    incident && incident.severity !== "none" ? incident.severity : "lengvas",
  );
  const [victimUser, setVictimUser] = useState(incident?.victimUser || "");
  const [occurredAt, setOccurredAt] = useState(
    msToLocalInput(incident?.occurredAt || null),
  );
  const [timeSinceShiftStart, setTimeSinceShiftStart] = useState(
    incident?.timeSinceShiftStart || "",
  );
  const [location, setLocation] = useState(incident?.location || "");
  const [description, setDescription] = useState(incident?.description || "");
  const [activityBefore, setActivityBefore] = useState(
    incident?.activityBefore || "",
  );
  const [causingFactor, setCausingFactor] = useState(
    incident?.causingFactor || "",
  );
  const [injuryNature, setInjuryNature] = useState(
    incident?.injuryNature || "",
  );
  const [bodyPart, setBodyPart] = useState(incident?.bodyPart || "");
  const [witnesses, setWitnesses] = useState<IncidentWitness[]>(
    incident?.witnesses || [],
  );
  const [causes, setCauses] = useState(incident?.causes || "");
  const [preventiveMeasures, setPreventiveMeasures] = useState(
    incident?.preventiveMeasures || "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEventAtWork = type === "event_at_work";

  const addWitness = () => setWitnesses((w) => [...w, { name: "", notes: "" }]);
  const updateWitness = (i: number, key: "name" | "notes", value: string) =>
    setWitnesses((w) =>
      w.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)),
    );
  const removeWitness = (i: number) =>
    setWitnesses((w) => w.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!companyId) return;
    if (!victimUser) {
      setError(
        t("incident_victim_required", {
          defaultValue: "Affected employee is required.",
        }),
      );
      return;
    }
    if (!description.trim()) {
      setError(
        t("incident_description_required", {
          defaultValue: "Description is required.",
        }),
      );
      return;
    }

    const payload = {
      type,
      // Non-injury incidents must explicitly clear severity so a prior
      // serious/fatal value is not left on the server (JSON omits undefined).
      severity: isEventAtWork ? severity : ("none" as IncidentSeverity),
      victimUser,
      // Send an unambiguous epoch instant. `new Date("2026-07-24T14:30")` parses
      // the naive datetime-local value in the BROWSER's zone (the operator's wall
      // clock), so write and both read paths (fmtDateTime / msToLocalInput, also
      // browser-local) stay symmetric regardless of the server's timezone.
      occurredAt: occurredAt ? new Date(occurredAt).getTime() : null,
      timeSinceShiftStart,
      location,
      description: description.trim(),
      activityBefore,
      causingFactor,
      injuryNature: isEventAtWork ? injuryNature : "",
      bodyPart: isEventAtWork ? bodyPart : "",
      witnesses: witnesses
        .map((w) => ({ name: w.name.trim(), notes: (w.notes || "").trim() }))
        .filter((w) => w.name || w.notes),
      causes,
      preventiveMeasures,
    };

    try {
      setSaving(true);
      setError("");
      if (isEdit && incident) {
        // `area` is never sent on edit — see the prop doc: update.js doesn't
        // accept it, and an existing incident's sphere can't be re-shown or
        // re-chosen here anyway.
        await updateIncident(companyId, incident.id, payload);
        dispatch(setIsModalxOpen(false));
        dispatch(setDestroy());
        onSuccess?.();
      } else {
        const res = await createIncident(companyId, {
          ...payload,
          // HARD RULE (PR-2 §P1): omit `area` entirely with 0-1 spheres that
          // track incidents, so this request stays byte-for-byte what it was
          // before spheres existed.
          ...(showAreaField
            ? { area: selectedArea ? selectedArea.id : selectedAreaId }
            : {}),
        });
        dispatch(setIsModalxOpen(false));
        dispatch(setDestroy());
        onSuccess?.(res.incident.id);
      }
    } catch (err: any) {
      console.error("Failed to save incident", err);
      setError(
        resolveAreaErrorMessage(err, t) ||
          err?.response?.data?.message ||
          t("incident_save_failed", {
            defaultValue: "Failed to save the incident.",
          }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormLayout>
        {error && <Banner tone="critical">{error}</Banner>}

        {!isEdit && (
          <AreaFormField
            areas={areas}
            selectedArea={selectedArea}
            value={selectedAreaId}
            onChange={setSelectedAreaId}
            disabled={saving}
          />
        )}

        <FormLayout.Group>
          <Select
            label={t("incident_type", { defaultValue: "Type" })}
            options={[
              {
                label: t("incident_type_incident", {
                  defaultValue: "Incident (no health damage)",
                }),
                value: "incident",
              },
              {
                label: t("incident_type_event_at_work", {
                  defaultValue: "Event at work (injury)",
                }),
                value: "event_at_work",
              },
            ]}
            value={type}
            onChange={(v) => setType(v as IncidentType)}
            disabled={saving}
          />
          {isEventAtWork && (
            <Select
              label={t("incident_severity", { defaultValue: "Severity" })}
              options={[
                {
                  label: t("incident_severity_lengvas", {
                    defaultValue: "Light",
                  }),
                  value: "lengvas",
                },
                {
                  label: t("incident_severity_sunkus", {
                    defaultValue: "Serious",
                  }),
                  value: "sunkus",
                },
                {
                  label: t("incident_severity_mirtinas", {
                    defaultValue: "Fatal",
                  }),
                  value: "mirtinas",
                },
              ]}
              value={severity}
              onChange={(v) => setSeverity(v as IncidentSeverity)}
              disabled={saving}
            />
          )}
        </FormLayout.Group>

        {isEventAtWork &&
          (severity === "sunkus" || severity === "mirtinas") && (
            <Banner tone="warning">
              <Text as="p" variant="bodySm">
                {t("incident_severe_form_hint", {
                  defaultValue:
                    "Serious/fatal accidents are investigated by VDI. You can register the record, but the system will not let you generate or sign an internal act.",
                })}
              </Text>
            </Banner>
          )}

        <UserSelect
          value={victimUser}
          onChange={(v: string) => setVictimUser(v)}
          label="incident_victim"
          isRequired
          onlyActivated
          showUserEmail
        />

        <FormLayout.Group>
          <TextField
            label={t("incident_occurred_at", { defaultValue: "Date and time" })}
            type="datetime-local"
            value={occurredAt}
            onChange={setOccurredAt}
            autoComplete="off"
            disabled={saving}
          />
          <TextField
            label={t("incident_time_since_shift", {
              defaultValue: "Time since shift start",
            })}
            value={timeSinceShiftStart}
            onChange={setTimeSinceShiftStart}
            autoComplete="off"
            disabled={saving}
            placeholder="2 val. 30 min."
          />
        </FormLayout.Group>

        <TextField
          label={t("incident_location", { defaultValue: "Location / unit" })}
          value={location}
          onChange={setLocation}
          autoComplete="off"
          disabled={saving}
        />

        <TextField
          label={t("incident_description", { defaultValue: "What happened" })}
          value={description}
          onChange={setDescription}
          multiline={3}
          autoComplete="off"
          disabled={saving}
          requiredIndicator
        />

        <TextField
          label={t("incident_activity_before", {
            defaultValue: "Activity before the event",
          })}
          value={activityBefore}
          onChange={setActivityBefore}
          multiline={2}
          autoComplete="off"
          disabled={saving}
        />

        <TextField
          label={t("incident_causing_factor", {
            defaultValue: "Causing factor / object",
          })}
          value={causingFactor}
          onChange={setCausingFactor}
          autoComplete="off"
          disabled={saving}
        />

        {isEventAtWork && (
          <FormLayout.Group>
            <TextField
              label={t("incident_injury_nature", {
                defaultValue: "Injury nature",
              })}
              value={injuryNature}
              onChange={setInjuryNature}
              autoComplete="off"
              disabled={saving}
            />
            <TextField
              label={t("incident_body_part", {
                defaultValue: "Injured body part",
              })}
              value={bodyPart}
              onChange={setBodyPart}
              autoComplete="off"
              disabled={saving}
            />
          </FormLayout.Group>
        )}

        <div className="border-t border-gray-150 pt-3">
          <Text as="p" variant="bodySm" fontWeight="semibold">
            {t("incident_witnesses", { defaultValue: "Witnesses" })}
          </Text>
          {witnesses.map((w, i) => (
            <div
              key={i}
              className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end mt-2"
            >
              <TextField
                label={t("incident_witness_name", { defaultValue: "Name" })}
                value={w.name}
                onChange={(v) => updateWitness(i, "name", v)}
                autoComplete="off"
                disabled={saving}
              />
              <div className="flex gap-2 items-end">
                <div className="min-w-0 flex-1">
                  <TextField
                    label={t("incident_witness_notes", { defaultValue: "Notes" })}
                    value={w.notes || ""}
                    onChange={(v) => updateWitness(i, "notes", v)}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>
                <Button onClick={() => removeWitness(i)} disabled={saving}>
                  {t("remove", { defaultValue: "Remove" })}
                </Button>
              </div>
            </div>
          ))}
          <div className="mt-2">
            <Button onClick={addWitness} disabled={saving}>
              {t("incident_add_witness", { defaultValue: "Add witness" })}
            </Button>
          </div>
        </div>

        <TextField
          label={t("incident_causes", { defaultValue: "Causes" })}
          value={causes}
          onChange={setCauses}
          multiline={2}
          autoComplete="off"
          disabled={saving}
        />

        <TextField
          label={t("incident_prevention", {
            defaultValue: "Preventive measures",
          })}
          value={preventiveMeasures}
          onChange={setPreventiveMeasures}
          multiline={2}
          autoComplete="off"
          disabled={saving}
        />

        <div className="flex justify-end gap-3 mt-4 border-t border-gray-150 pt-4">
          <Button
            onClick={() => {
              dispatch(setIsModalxOpen(false));
              dispatch(setDestroy());
            }}
            disabled={saving}
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>
            {t("save", { defaultValue: "Save" })}
          </Button>
        </div>
      </FormLayout>
    </Form>
  );
}
